import { z } from 'zod';
import { idSchema, moneySchema } from './common.schema.js';
import { INVOICE_STATUSES } from './enums.js';
import { projectSchema } from './project.schema.js';
/** One bar on the owner's "koszty budów" panel, broken down by category. */
export const projectCostBreakdownSchema = z.object({
    categoryId: idSchema.nullable(),
    categoryName: z.string(),
    netAmount: moneySchema,
});
export const projectCostSummarySchema = z.object({
    projectId: idSchema.nullable(),
    projectCode: z.string(),
    projectName: z.string(),
    contractValue: moneySchema.nullable(),
    netAmount: moneySchema,
    invoiceCount: z.number().int(),
    breakdown: z.array(projectCostBreakdownSchema),
});
export const vendorCostSummarySchema = z.object({
    vendorId: idSchema,
    vendorName: z.string(),
    netAmount: moneySchema,
    invoiceCount: z.number().int(),
});
/** The four tiles on the owner's dashboard. */
export const dashboardSummarySchema = z.object({
    awaitingMyApproval: z.object({ count: z.number().int(), amount: moneySchema }),
    overdue: z.object({
        count: z.number().int(),
        amount: moneySchema,
        maxDaysLate: z.number().int(),
    }),
    awaitingManagers: z.object({
        count: z.number().int(),
        maxDaysWaiting: z.number().int(),
    }),
    currentMonth: z.object({ netAmount: moneySchema, invoiceCount: z.number().int() }),
});
export const activityEntrySchema = z.object({
    id: idSchema,
    /** Who performed the change. */
    userName: z.string(),
    /** The invoice's vendor — the "kontrahent" the row is about. */
    vendorName: z.string(),
    invoiceNumber: z.string(),
    amount: moneySchema,
    /** The status the invoice moved into. */
    status: z.enum(INVOICE_STATUSES),
    createdAt: z.coerce.date(),
});
/** One stage of the invoice flow: how many sit there and for how much. */
export const invoiceStatusBucketSchema = z.object({
    status: z.enum(INVOICE_STATUSES),
    count: z.number().int(),
    grossAmount: moneySchema,
});
export const INVOICE_FLAGS = ['CASH', 'MISSING_PROTOCOL', 'WITHOUT_OWNER'];
/**
 * Invoices that need a second look regardless of status: paid in cash (so
 * absent from payables), still missing their handover protocol, or released
 * for payment by an accountant in the owner's place.
 */
export const invoiceFlagBucketSchema = z.object({
    flag: z.enum(INVOICE_FLAGS),
    count: z.number().int(),
    grossAmount: moneySchema,
});
/** One overdue bucket of the payables aging chart. */
export const agingBucketSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
    amountDue: moneySchema,
});
export const reportQuerySchema = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    projectId: idSchema.optional(),
});
/* ──────────────────────── One site's cost dashboard ─────────────────────── */
/** One month on a site's cost trend. */
export const monthlyCostPointSchema = z.object({
    /** `YYYY-MM` — sorts as text and carries no timezone to get wrong. */
    month: z.string(),
    netAmount: moneySchema,
    invoiceCount: z.number().int(),
});
export const categoryCostSummarySchema = z.object({
    categoryId: idSchema.nullable(),
    categoryName: z.string(),
    netAmount: moneySchema,
    invoiceCount: z.number().int(),
});
export const statusCostSummarySchema = z.object({
    status: z.enum(INVOICE_STATUSES),
    count: z.number().int(),
    netAmount: moneySchema,
});
/**
 * Everything the site screen shows above its invoice list.
 *
 * Costs are read from the allocations, so a split invoice charges each site
 * only its own slice; the payment figures are read from the invoices those
 * allocations belong to, because a term and a transfer are per document.
 */
export const projectDashboardSchema = z.object({
    project: projectSchema,
    totals: z.object({
        invoiceCount: z.number().int(),
        netAmount: moneySchema,
        grossAmount: moneySchema,
        unpaid: z.object({ count: z.number().int(), amount: moneySchema }),
        overdue: z.object({
            count: z.number().int(),
            amount: moneySchema,
            maxDaysLate: z.number().int(),
        }),
        awaitingManager: z.object({ count: z.number().int() }),
    }),
    /**
     * `null` when the site has no contract value recorded — no budget, no bar.
     * Always read over the site's whole life, never through the date filter: a
     * contract is not signed per quarter.
     */
    budget: z
        .object({
        contractValue: moneySchema,
        /** Net booked on the site since it started, ignoring `from`/`to`. */
        netAmount: moneySchema,
        /** Above 1 means the site has overrun what was contracted. */
        usedShare: z.number(),
    })
        .nullable(),
    byCategory: z.array(categoryCostSummarySchema),
    /** Every vendor, sorted by cost — the screen decides how many to draw. */
    byVendor: z.array(vendorCostSummarySchema),
    byMonth: z.array(monthlyCostPointSchema),
    byStatus: z.array(statusCostSummarySchema),
});
export const projectDashboardQuerySchema = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
//# sourceMappingURL=report.schema.js.map